import intl from "react-intl-universal";

import {AppState} from "@stores/AppState";
import {WorkspaceModelType} from "@stores/WorkspaceStore";
import {updateItemChildIdsAdd, updateItemChildIdsRemove} from "./updateItem";
import {updateWorkspaceChildIdsRemove} from "../workspace/updateWorkspace";
import {UpdateItemChildIdsRemoveUndoable} from "./undoable/updateItemChildIdsRemoveUndoable";
import {showInWorkspace} from "../ui/showInWorkspace";
import {UndoableAction} from "../common/UndoableAction";
import {UndoableActionGroup} from "../common/UndoableActionGroup";
import {undoableFunctionCall} from "../common/undoableFunctionCall";
import {CrudServices} from "../../platform/services/types/ServiceDependencies";


export class UndoableAddToCluster implements UndoableAction {
    args: Parameters<typeof addToCluster>;
    undoableGroup: UndoableActionGroup;

    constructor(...args: Parameters<typeof addToCluster>) {
        this.undoableGroup = new UndoableActionGroup();
        this.args = args;
    }

    execute(): Promise<any> {
        /* @ts-ignore the last argument of moveItem is not used in initial constructor and exists only for this use-case */
        return addToCluster.apply(null, [...this.args, this.undoableGroup]);
    }

    undo(): Promise<any> {
        return this.undoableGroup.undo();
    }
}


export const undoableAddToCluster = (
    services: CrudServices,
    appState: AppState,
    itemId: string,
    targetClusterId: string,
    workspace: WorkspaceModelType
) => {
    const undoableAction = new UndoableAddToCluster(services, appState, itemId, targetClusterId, workspace);
    return undoableFunctionCall(undoableAction, intl.get('snackbar.add_to_cluster').d('Page added to cluster.'));
}

export const addToCluster = (
    services: CrudServices,
    appState: AppState,
    itemId: string,
    targetClusterId: string,
    workspace: WorkspaceModelType,
    undoableActionGroup?: UndoableActionGroup,
) => {
    const dsManager = appState.userState!.dsManager!;
    const clusterMST = workspace.items.get(targetClusterId)!;
    const itemMST = workspace.items.get(itemId)!;
    const wsUpdateServices = {...services, dsManager};

    const removals: Promise<any>[] = [];

    if (itemMST.parentIds.length === 0) {
        // Legacy: item at workspace root (no libraries)
        removals.push(updateWorkspaceChildIdsRemove(wsUpdateServices, workspace, itemMST.id, undoableActionGroup));
    } else {
        // If every current parent is a LIBRARY, the item sits only at library-root level.
        // Move it out of those library parents so it only appears inside the target cluster.
        const libraryParentIds = itemMST.parentIds.filter(id => workspace.items.get(id)?.isLibrary === true);
        if (libraryParentIds.length === itemMST.parentIds.length) {
            for (const libraryId of libraryParentIds) {
                if (undoableActionGroup !== undefined) {
                    removals.push(undoableActionGroup.add(
                        new UpdateItemChildIdsRemoveUndoable(services, dsManager, libraryId, itemId, workspace)
                    ));
                } else {
                    removals.push(updateItemChildIdsRemove(services, dsManager, libraryId, itemId, workspace));
                }
            }
        }
        // else: item is already in sub-clusters — keep multi-cluster behavior unchanged
    }

    const updateItemChildsWait = updateItemChildIdsAdd(services, dsManager, clusterMST, itemId, workspace, undefined, undoableActionGroup);
    showInWorkspace(appState.rootStore, itemId, workspace);

    return Promise.all([...removals, updateItemChildsWait]);
}
